import { ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ChatPanel } from '../ai/ChatPanel';
import { SystemBannerBar } from '../system/SystemBannerBar';
import { EmailIntakeAutoCreatedToasts } from '../../../pages/email-intake/EmailIntakeAutoCreatedToasts';
import { useMyPermissions } from '../../../hooks/use-permissions';
import { useAuthStore } from '../../../stores/auth-store';
import api, { setSuppressAuthRedirect } from '../../../lib/api';

const IDLE_TIMEOUT_MS = 0; // DISABLED — set to 90_000 to re-enable demo auto-reset

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const queryClient = useQueryClient();
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [resetting, setResetting] = useState(false);

  // Fetch & sync current user's granular permissions into the store
  useMyPermissions();

  // Scroll main content area to top on route change
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // ── Idle auto-refresh: reset demo data after inactivity ──
  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!IDLE_TIMEOUT_MS) return; // disabled when 0
    idleTimer.current = setTimeout(async () => {
      setResetting(true);
      // Suppress 401 redirects during the entire reset process
      setSuppressAuthRedirect(true);
      try {
        // This call runs the seed script — takes 30-60 seconds
        await api.post('/settings/demo/reset');
      } catch {
        // Ignore errors — partial seed is fine
      }
      // The seed deletes and recreates the org+users, so the current JWT is invalid.
      // Re-login silently with demo credentials using the auth store.
      try {
        const loginRes = await api.post('/auth/login', {
          email: 'admin@demo.tracxtms.com',
          password: 'demo1234',
        });
        const data = loginRes.data?.data || loginRes.data;
        const user = data?.user;
        const tokens = data?.tokens;
        if (user && tokens?.accessToken) {
          // Use the auth store's login method — this sets localStorage AND zustand state
          useAuthStore.getState().login(user, tokens.accessToken);
          if (tokens.refreshToken) {
            localStorage.setItem('refreshToken', tokens.refreshToken);
          }
        }
      } catch {
        // If re-login fails, user will need to log in manually
      }
      // Re-enable 401 redirect now that we have a fresh token
      setSuppressAuthRedirect(false);
      // Invalidate all data queries so components re-fetch with fresh data
      await queryClient.invalidateQueries();
      // Navigate to dispatch screen after reset
      navigate('/dispatch');
      setResetting(false);
    }, IDLE_TIMEOUT_MS);
  }, [queryClient, navigate]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetIdleTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer(); // start on mount
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer]);

  // Global keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;

      if (e.key === 'Escape') return;
      if (isInput) return;

      if (e.key === 'n' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        navigate('/dispatch-loads');
      }

      if (e.key === '/') {
        e.preventDefault();
        const searchInput = document.getElementById(
          'global-search'
        ) as HTMLInputElement | null;
        if (searchInput) {
          searchInput.focus();
        } else {
          const searchBtn = document.querySelector(
            '[data-search-trigger]'
          ) as HTMLButtonElement | null;
          searchBtn?.click();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return (
    <div style={{ backgroundColor: 'var(--vz-body-bg)', minHeight: '100vh', color: 'var(--vz-body-color)', transition: 'background-color 0.2s, color 0.2s' }}>
      <Header />
      <Sidebar />
      {/* ATL-001483 — toast when a mailbox auto-creates a load from an email. */}
      <EmailIntakeAutoCreatedToasts />
      <main
        ref={mainRef}
        className="relative overflow-y-auto"
        style={{
          /* header 50px + subnav 46px = 96px (top), footer 24px (bottom). */
          paddingTop: '96px',
          paddingBottom: '24px',
          /* `height` (not `minHeight`) so <main> is the scroll container and
             scrolls its content internally, instead of growing and letting
             the WINDOW scroll. The latter breaks `position: sticky` inside
             pages (the sticky's scroll ancestor — main — scrolls out of view
             with the window). Standard fixed-header + scrolling-main pattern. */
          height: '100vh',
        }}
      >
        <SystemBannerBar />
        <div style={{ padding: '0 12px 60px 12px' }}>{children}</div>
        <footer style={{
          padding: '8px 12px',
          textAlign: 'right',
          fontSize: '11px',
          color: 'var(--vz-secondary-color, #999)',
        }}>
          <a
            href="https://www.tracxtms.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {t('components.appShell.masterServiceAgreement')}
          </a>
          <span style={{ margin: '0 8px' }} aria-hidden>{'·'}</span>
          {t('components.appShell.version', { revision: typeof __BUILD_REVISION__ !== 'undefined' ? __BUILD_REVISION__ : '2.0.0' })}
        </footer>
      </main>
      {/* Fixed-position brand footer — matches header navy, stays pinned
          to the bottom of the viewport regardless of page length. */}
      <footer
        className="fixed bottom-0 left-0 right-0 z-[1001] flex items-center justify-between"
        style={{
          height: '24px',
          padding: '0 16px',
          backgroundColor: '#0D1E33',
          color: 'rgba(255,255,255,0.78)',
          fontSize: '10.5px',
          fontWeight: 500,
          letterSpacing: '0.2px',
        }}
      >
        <span>
          {t('components.appShell.copyright', { year: new Date().getFullYear() })}
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {t('components.appShell.revision', { revision: typeof __BUILD_REVISION__ !== 'undefined' ? __BUILD_REVISION__ : '2.0.0' })}
        </span>
      </footer>
      {/* Idle auto-refresh overlay */}
      {resetting && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            backgroundColor: 'var(--vz-card-bg, #fff)',
            borderRadius: '16px', padding: '32px 48px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <Loader2 style={{ width: '36px', height: '36px', color: '#468AD4', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '16px', fontWeight: 500, color: 'var(--vz-heading-color, #333)' }}>
              {t('components.appShell.refreshingDemoData')}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--vz-body-color, #666)' }}>
              {t('components.appShell.pleaseWaitReset')}
            </span>
          </div>
        </div>
      )}
      <ChatPanel />
    </div>
  );
}
